/**
 * Portable text for agents (ADR-009 §3.1 `text.set`, §6.3 "portable text and
 * image preparation"): the asynchronous path the inspector and the New
 * portable text dialog use, split at the await.
 *
 * `prepare` shapes in the portable-text worker under `captureRevision()`
 * (`preparePortableTextEdit` / `preparePortableTextCreation`), outside
 * history; `compile` turns the prepared result into the same native commands
 * (`EditPortableTextCommand`, the resource-layer command of
 * `createPortableText`) inside one `TransactionCommand`. The dispatcher
 * re-checks the revision ledger after prepare, and compile re-checks that the
 * layer, its text, the document's portable-text activations and its resource
 * metadata are exactly as shaped, so a prepared layout is never applied over
 * a newer edit.
 *
 * This module is loaded by the UI's synchronous path (via the command
 * registry), so the public-format and worker modules are imported lazily.
 * Font file import is not offered: agents choose among fonts already embedded
 * in the document (`{ existing: fontId }`).
 */
import type { LayerId, TextSetIntent } from '@maipo/agent-protocol';
import { AddLayerCommand, changed, type Command, type CommandExecution } from '../../core/commands';
import { damage } from '../../core/damage';
import { findLayer, type EditorDocument, type TextLayer } from '../../core/model';
import type { DocumentSession } from '../../core/session';
import type { PortableTextEdit, PreparedPortableTextEdit } from '../../public-format/edit-portable-text';
import type { PreparedResourceLayer } from '../../public-format/transfer-layer';
import { IntentRejection, type Diagnostic, type ValidateContext } from '../registry';
import { checkUnlocked, error, isAlias, staticIds, validatePlacement } from '../intents/common';
import { defineCommand, DOCUMENT_RESOURCES } from './registry';

function isAbort(error: unknown, signal: AbortSignal): boolean {
  return signal.aborted || (error instanceof Error && error.name === 'AbortError');
}

/** Turns a native rejection or an invalid-field error into a structured rejection. */
function rejection(error: unknown, layer?: LayerId): IntentRejection {
  if (error instanceof IntentRejection) return error;
  const message = error instanceof Error ? error.message : 'Portable text could not be prepared.';
  return new IntentRejection(/locked/i.test(message) ? 'LAYER_LOCKED' : 'COMMAND_REJECTED', message, layer);
}

function rejected(execution: CommandExecution, layer?: LayerId): IntentRejection {
  const reason = execution.outcome === 'rejected' ? execution.reason : 'Portable text could not be prepared.';
  return new IntentRejection(/locked/i.test(reason) ? 'LAYER_LOCKED' : 'COMMAND_REJECTED', reason, layer);
}

/** Replaces a text payload on a dry-run clone (no paint state exists there). */
class SetTextPayloadCommand implements Command {
  readonly label = 'Edit portable text';
  private before?: TextLayer['text'];
  constructor(private readonly layerId: LayerId, private readonly after: TextLayer['text']) {}
  retainedBytes(): number { return 256 + JSON.stringify(this.after).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'text') throw new IntentRejection('LAYER_NOT_FOUND', `Layer ${this.layerId} does not exist.`, this.layerId);
    this.before ??= structuredClone(layer.text);
    layer.text = structuredClone(this.after);
    return changed(this.damage());
  }
  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'text' && this.before) layer.text = structuredClone(this.before);
  }
  damage() { return damage({ layerIds: [this.layerId] }); }
}

// ---------------------------------------------------------------------------
// text.set on portable text (called from intents/content.ts)

/** The layer `ref` names is portable text (schemaVersion 2). Aliases never are: no intent creates portable text. */
export function isPortableTextTarget(document: EditorDocument, ref: string): boolean {
  if (isAlias(ref)) return false;
  const layer = findLayer(document.root, ref);
  return layer?.kind === 'text' && layer.text.schemaVersion === 2;
}

/**
 * Portable text has a fixed frame, left alignment, zero tracking and metric
 * line spacing, and takes its family and weight from its primary font. Fields
 * the format fixes are accepted only at their current value.
 */
export function validatePortableTextSet(document: EditorDocument, layer: TextLayer, intent: TextSetIntent, context: ValidateContext, diagnostics: Diagnostic[]): void {
  const style = intent.style ?? {};
  for (const key of ['family', 'weight', 'lineHeight', 'tracking', 'align'] as const) {
    if (style[key] !== undefined && style[key] !== layer.text.style[key]) {
      diagnostics.push(error('UNSUPPORTED_FOR_KIND', key === 'family' || key === 'weight'
        ? `Portable text ${layer.id} takes its family and weight from its primary font; set portable.fonts to an embedded font instead.`
        : `Portable text ${layer.id} has fixed ${key}; only content, size, color, box width/height and portable settings can change.`, layer.id));
      return;
    }
  }
  if (intent.box?.autoHeight === true) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Portable text ${layer.id} has a fixed frame; autoHeight is not available.`, layer.id));
  if (intent.content !== undefined && Array.from(intent.content).length > 10_000) diagnostics.push(error('LIMIT_EXCEEDED', 'Portable text is limited to 10,000 characters.', layer.id));
  // The inspector refuses a locked layer for everyone; agents also respect locked groups.
  if (layer.locked) diagnostics.push(error('LAYER_LOCKED', `Layer ${layer.id} is locked.`, layer.id));
  else checkUnlocked(document, layer, 'any', diagnostics, context.policy);
}

function portableEdit(layer: TextLayer, intent: TextSetIntent): PortableTextEdit {
  const edit: PortableTextEdit = {};
  if (intent.content !== undefined) edit.value = intent.content;
  if (intent.style?.size !== undefined) edit.size = intent.style.size;
  if (intent.style?.color !== undefined) {
    // Re-setting the stored colour in another letter case must not rewrite bytes.
    edit.color = intent.style.color.toLowerCase() === layer.text.style.color.toLowerCase() ? layer.text.style.color : intent.style.color.toLowerCase();
  }
  if (intent.box?.width !== undefined) edit.width = intent.box.width;
  if (intent.box?.height !== undefined) edit.height = intent.box.height;
  const portable = intent.portable ?? {};
  if (portable.fonts !== undefined) edit.fonts = [...portable.fonts];
  if (portable.lineGap !== undefined) edit.lineGap = portable.lineGap;
  if (portable.baseDirection !== undefined) edit.baseDirection = portable.baseDirection;
  if (portable.language !== undefined) edit.language = portable.language;
  return edit;
}

export type PortableTextSetPrepared = { kind: 'no-op' } | { kind: 'edit'; edit: PreparedPortableTextEdit };

/** Shapes the edit in the worker (the inspector's `editPortableText` up to its commit). */
export async function preparePortableTextSet(session: DocumentSession, intent: TextSetIntent, signal: AbortSignal): Promise<PortableTextSetPrepared> {
  const layer = findLayer(session.document.root, intent.layer);
  if (layer?.kind !== 'text' || layer.text.schemaVersion !== 2) throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${intent.layer} is not portable text.`, intent.layer);
  const { preparePortableTextEdit } = await import('../../public-format/edit-portable-text');
  let result: Awaited<ReturnType<typeof preparePortableTextEdit>>;
  try {
    result = await preparePortableTextEdit(session, layer.id, portableEdit(layer, intent), signal);
  } catch (caught) {
    if (isAbort(caught, signal)) throw caught;
    throw rejection(caught, layer.id);
  }
  if ('outcome' in result) {
    if (result.outcome === 'no-op') return { kind: 'no-op' };
    throw rejected(result, layer.id);
  }
  return { kind: 'edit', edit: result };
}

export function compilePortableTextSet(document: EditorDocument, layer: TextLayer, prepared: unknown): Command[] {
  const value = prepared as PortableTextSetPrepared | undefined;
  if (!value) throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Portable text ${layer.id} must be addressed by its id, not by an alias.`, layer.id);
  if (value.kind === 'no-op') return [];
  const { edit } = value;
  // A dry run executes on a clone of the document, which has no paint state.
  if (document !== edit.document) return [new SetTextPayloadCommand(layer.id, edit.after)];
  if (!edit.matches(document)) throw new IntentRejection('CONFLICT', `Portable text ${layer.id} changed while it was shaped. Read it again and retry.`, layer.id);
  return [edit.command()];
}

export function releasePortableTextSet(prepared: unknown): void {
  const value = prepared as PortableTextSetPrepared | undefined;
  if (value?.kind === 'edit') value.edit.release();
}

// ---------------------------------------------------------------------------
// text.createPortable

export interface CreatePortableParams {
  content: string;
  fonts: { existing: string }[];
  width: number;
  height: number;
  size: number;
  color: string;
  baseDirection?: 'auto' | 'ltr' | 'rtl';
  language?: string;
  at?: { x: number; y: number };
  parent?: string;
  index?: number;
}

function placementOf(params: CreatePortableParams) {
  return { ...(params.parent === undefined ? {} : { parent: params.parent }), ...(params.index === undefined ? {} : { index: params.index }) };
}

export const createPortable = defineCommand<CreatePortableParams>({
  id: 'text.createPortable',
  createsLayer: true,
  group: 'text',
  backs: ['action.new-portable-text', 'dialog.create-portable-text'],
  asynchronous: true,
  label: () => 'Create portable text',
  footprint: (_document, params, aliases) => {
    const keys = params.parent === undefined || params.parent === 'root' ? ['root'] : staticIds([params.parent], aliases);
    return { reads: [...keys, DOCUMENT_RESOURCES.publicResources], writes: [...keys, DOCUMENT_RESOURCES.publicResources], structural: true, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (params.parent !== undefined && params.parent !== 'root' && isAlias(params.parent)) {
      diagnostics.push(error('COMMAND_REJECTED', 'text.createPortable needs an existing parent group id: it is shaped before earlier intents run.'));
      return diagnostics;
    }
    validatePlacement(document, placementOf(params), context, diagnostics);
    if (Array.from(params.content).length > 10_000) diagnostics.push(error('LIMIT_EXCEEDED', 'Portable text is limited to 10,000 characters.'));
    return diagnostics;
  },
  prepare: async (session, params, signal) => {
    const { preparePortableTextCreation } = await import('../../public-format/create-portable-text');
    const parent = params.parent === undefined || params.parent === 'root' ? 'root' : params.parent;
    let result: Awaited<ReturnType<typeof preparePortableTextCreation>>;
    try {
      result = await preparePortableTextCreation(session, {
        value: params.content,
        fonts: params.fonts.map((font) => font.existing),
        width: params.width,
        height: params.height,
        size: params.size,
        color: params.color,
        ...(params.baseDirection === undefined ? {} : { baseDirection: params.baseDirection }),
        ...(params.language === undefined ? {} : { language: params.language }),
        parentId: parent,
        index: params.index ?? 0,
        ...(params.at ? { transform: [1, 0, 0, 1, params.at.x, params.at.y] as const } : {}),
      }, signal);
    } catch (caught) {
      if (isAbort(caught, signal)) throw caught;
      throw rejection(caught);
    }
    if ('outcome' in result) throw rejected(result);
    return result;
  },
  compile: (document, _params, prepared) => {
    const value = prepared as PreparedResourceLayer | undefined;
    if (!value) throw new IntentRejection('COMMAND_REJECTED', 'Portable text was not prepared.');
    // A dry run executes on a clone; it shows the layer without its paint or resource state.
    if (document !== value.target) return [new AddLayerCommand(structuredClone(value.layer), value.parentId, value.index)];
    if (!value.matches(document)) throw new IntentRejection('CONFLICT', 'The document\'s embedded resources changed while the text was shaped. Retry.');
    return [value.command()];
  },
  releasePrepared: (_params, prepared) => (prepared as PreparedResourceLayer | undefined)?.release(),
});

export const TEXT_COMMANDS = [createPortable] as const;
